var __defProp = Object.defineProperty;
var __typeError = (msg) => {
  throw TypeError(msg);
};
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateMethod = (obj, member, method) => (__accessCheck(obj, member, "access private method"), method);
var _debouncedOnChange, _CartItemsComponent_instances, onQuantityChange_fn, _handleCartError, _handleCartUpdate, disableCartItems_fn, enableCartItems_fn, updateQuantitySelectors_fn, updateCartQuantitySelectorButtonStates_fn;
import { Component } from "@theme/component";
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition
} from "@theme/utilities";
import { morphSection, sectionRenderer } from "@theme/section-renderer";
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent
} from "@theme/events";
import { cartPerformance } from "@theme/performance";
class CartItemsComponent extends Component {
  constructor() {
    super(...arguments);
    __privateAdd(this, _CartItemsComponent_instances);
    __privateAdd(this, _debouncedOnChange, debounce(__privateMethod(this, _CartItemsComponent_instances, onQuantityChange_fn), 300).bind(this));
    /**
     * Handles the discount update.
     * @param {DiscountUpdateEvent} event - The event.
     */
    __publicField(this, "handleDiscountUpdate", (event) => {
      __privateGet(this, _handleCartUpdate).call(this, event);
    });
    /**
     * Handles the cart error.
     * @param {number} line - The line.
     * @param {Object} parsedResponseText - The parsed response text.
     * @param {string} parsedResponseText.errors - The errors.
     */
    __privateAdd(this, _handleCartError, (line, parsedResponseText) => {
      const quantitySelector = this.refs.quantitySelectors[line - 1];
      const quantityInput = quantitySelector?.querySelector("input");
      if (!quantityInput) throw new Error("Quantity input not found");
      quantityInput.value = quantityInput.defaultValue;
      const cartItemError = this.refs[`cartItemError-${line}`];
      const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];
      if (!(cartItemError instanceof HTMLElement)) throw new Error("Cart item error not found");
      if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error("Cart item error container not found");
      cartItemError.textContent = parsedResponseText.errors;
      cartItemErrorContainer.classList.remove("hidden");
    });
    /**
     * Handles the cart update.
     *
     * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
     */
    __privateAdd(this, _handleCartUpdate, (event) => {
      if (event instanceof DiscountUpdateEvent) {
        sectionRenderer.renderSection(this.sectionId, { cache: false });
        return;
      }
      if (event.target === this) return;
      const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
      if (cartItemsHtml) {
        morphSection(this.sectionId, cartItemsHtml, "full", { injectStylesheet: true });
        __privateMethod(this, _CartItemsComponent_instances, updateCartQuantitySelectorButtonStates_fn).call(this);
      } else {
        sectionRenderer.renderSection(this.sectionId, { cache: false });
      }
    });
  }
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, __privateGet(this, _handleCartUpdate));
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, __privateGet(this, _debouncedOnChange));
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, __privateGet(this, _handleCartUpdate));
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, __privateGet(this, _debouncedOnChange));
  }
  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: "clear"
    });
    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];
    if (!cartItemRowToRemove) return;
    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key)
    ];
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;
    const template = document.getElementById("empty-cart-template");
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);
      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? "empty-cart-drawer" : "empty-cart-page"]);
      return;
    }
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();
      if (prefersReducedMotion()) return remove();
      row.style.setProperty("--row-height", `${row.clientHeight}px`);
      row.classList.add("removing");
      onAnimationEnd(row, remove);
    });
  }
  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    __privateMethod(this, _CartItemsComponent_instances, disableCartItems_fn).call(this);
    const { line, quantity } = config;
    const { cartTotal } = this.refs;
    const cartItemsComponents = document.querySelectorAll("cart-items-component");
    const sectionsToUpdate = /* @__PURE__ */ new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });
    const body = JSON.stringify({
      line,
      quantity,
      sections: Array.from(sectionsToUpdate).join(","),
      sections_url: window.location.pathname
    });
    cartTotal?.shimmer();
    fetch(`${Theme.routes.cart_change_url}`, fetchConfig("json", { body })).then((response) => {
      return response.text();
    }).then((responseText) => {
      const parsedResponseText = JSON.parse(responseText);
      resetShimmer(this);
      if (parsedResponseText.errors) {
        __privateGet(this, _handleCartError).call(this, line, parsedResponseText);
        return;
      }
      const newSectionHTML = new DOMParser().parseFromString(
        parsedResponseText.sections[this.sectionId],
        "text/html"
      );
      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;
      __privateMethod(this, _CartItemsComponent_instances, updateQuantitySelectors_fn).call(this, parsedResponseText);
      this.dispatchEvent(
        new CartUpdateEvent(parsedResponseText, this.sectionId, {
          itemCount: newCartItemCount,
          source: "cart-items-component",
          sections: parsedResponseText.sections
        })
      );
      morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], this.isDrawer ? "hydration" : "full", { injectStylesheet: true });
      __privateMethod(this, _CartItemsComponent_instances, updateCartQuantitySelectorButtonStates_fn).call(this);
    }).catch((error) => {
      console.error(error);
    }).finally(() => {
      __privateMethod(this, _CartItemsComponent_instances, enableCartItems_fn).call(this);
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    });
  }
  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;
    if (!sectionId) throw new Error("Section id missing");
    return sectionId;
  }
  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== void 0;
  }
}
_debouncedOnChange = new WeakMap();
_CartItemsComponent_instances = new WeakSet();
/**
 * Handles QuantitySelectorUpdateEvent change event.
 * @param {QuantitySelectorUpdateEvent} event - The event.
 */
onQuantityChange_fn = function(event) {
  if (!(event.target instanceof Node) || !this.contains(event.target)) return;
  const { quantity, cartLine: line } = event.detail;
  if (!line) return;
  if (quantity === 0) {
    return this.onLineItemRemove(line);
  }
  this.updateQuantity({
    line,
    quantity,
    action: "change"
  });
  const lineItemRow = this.refs.cartItemRows[line - 1];
  if (!lineItemRow) return;
  const textComponent = (
    /** @type {TextComponent | undefined} */
    lineItemRow.querySelector("text-component")
  );
  textComponent?.shimmer();
};
_handleCartError = new WeakMap();
_handleCartUpdate = new WeakMap();
/**
 * Disables the cart items.
 */
disableCartItems_fn = function() {
  this.classList.add("cart-items-disabled");
};
/**
 * Enables the cart items.
 */
enableCartItems_fn = function() {
  this.classList.remove("cart-items-disabled");
};
/**
 * Updates quantity selectors for all matching variants in the cart.
 * @param {Object} updatedCart - The updated cart object.
 * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
 */
updateQuantitySelectors_fn = function(updatedCart) {
  if (!updatedCart.items) return;
  for (const item of updatedCart.items) {
    const variantId = item.variant_id.toString();
    const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);
    for (const selector of selectors) {
      const input = selector.querySelector("input[data-cart-quantity]");
      if (!input) continue;
      input.setAttribute("data-cart-quantity", item.quantity.toString());
      if ("updateCartQuantity" in selector && typeof selector.updateCartQuantity === "function") {
        selector.updateCartQuantity();
      }
    }
  }
};
/**
 * Updates button states for all cart quantity selector components.
 */
updateCartQuantitySelectorButtonStates_fn = function() {
  for (const selector of document.querySelectorAll("cart-quantity-selector-component")) {
    selector.updateButtonStates?.();
  }
};
if (!customElements.get("cart-items-component")) {
  customElements.define("cart-items-component", CartItemsComponent);
}
